/* MarketingMobileNav — hamburger button + slide-out sheet for mobile.
 *
 * Used on every marketing surface (/, /buyers, /about, /faq, /contact,
 * /partners) so the nav is consistent and one-source-of-truth.
 *
 * Renders nothing above the `md` breakpoint — the parent header still
 * controls the desktop nav. On mobile (<768px) it shows a hamburger
 * button that opens a slide-out sheet with all nav links + a primary
 * "Get paid today" CTA pinned at the bottom.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

export default function MarketingMobileNav({
  // Primary nav items — array of { label, href, kind? } where kind = "section" uses anchor scroll,
  // otherwise it's a route. Default works for / and /buyers landings.
  items = DEFAULT_ITEMS,
  // Override the bottom CTA. Default is the seller "Get paid today" pill.
  cta = { label: "Get paid today", href: "/register?as=seller" },
  // Whether to show a secondary "Sign in" link below the CTA.
  showSignIn = true,
}) {
  const [open, setOpen] = useState(false);

  // Lock scroll while open so the page underneath doesn't move.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [open]);

  // Close on Escape — accessibility table-stakes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400"
        aria-label="Open menu"
        data-testid="marketing-mobile-nav-trigger"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Backdrop + sheet */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[100]" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
          />
          {/* Sheet — slides in from the right, 88vw wide capped at 360px */}
          <div className="absolute top-0 right-0 h-full w-[88vw] max-w-[360px] bg-white shadow-2xl flex flex-col animate-[slideInRight_220ms_ease-out]" data-testid="marketing-mobile-nav-sheet">
            {/* Close row */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-[10px] mono uppercase tracking-[0.22em] text-slate-500">Menu</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-700 hover:bg-slate-100"
                aria-label="Close menu"
                data-testid="marketing-mobile-nav-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Links — generous tap targets (56px row height) */}
            <nav className="flex-1 px-3 mt-2 overflow-y-auto" aria-label="Mobile navigation">
              <ul className="space-y-0.5">
                {items.map((it) => (
                  <li key={it.href}>
                    <NavItem item={it} onNavigate={() => setOpen(false)} />
                  </li>
                ))}
              </ul>
            </nav>

            {/* CTA + sign in pinned to the bottom */}
            <div className="border-t border-slate-200/80 p-5 space-y-3 bg-white">
              <a
                href={cta.href}
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 w-full h-12 rounded-md text-white text-[14px] font-medium bg-slate-900 hover:opacity-90 transition-opacity"
                data-testid="marketing-mobile-nav-cta"
              >
                {cta.label}
                <ArrowRight className="w-4 h-4" />
              </a>
              {showSignIn && (
                <a
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="block text-center text-[13px] text-slate-600 hover:text-slate-900 py-2"
                  data-testid="marketing-mobile-nav-signin"
                >
                  Sign in
                </a>
              )}
            </div>
          </div>

          {/* Keyframes inline so we don't have to touch the global stylesheet. */}
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to   { transform: translateX(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}

// Default items mirror the seller-landing primary nav.
export const DEFAULT_ITEMS = [
  { label: "For sellers", href: "/" },
  { label: "For buyers",  href: "/buyers" },
  { label: "About",       href: "/about" },
  { label: "Partners",    href: "/partners" },
  { label: "FAQ",         href: "/faq" },
  { label: "Contact",     href: "/contact" },
];

// One row in the sheet — handles both internal routes and anchor-style links.
function NavItem({ item, onNavigate }) {
  const isAnchor = item.href.startsWith("#");
  const klass = "flex items-center justify-between px-3 h-14 rounded-lg text-[15px] text-slate-800 hover:bg-slate-50 transition-colors";
  if (isAnchor) {
    return (
      <a href={item.href} onClick={onNavigate} className={klass} data-testid={`marketing-mobile-nav-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
        <span>{item.label}</span>
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </a>
    );
  }
  return (
    <Link to={item.href} onClick={onNavigate} className={klass} data-testid={`marketing-mobile-nav-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span>{item.label}</span>
      <ArrowRight className="w-4 h-4 text-slate-400" />
    </Link>
  );
}
